/* eslint-disable @typescript-eslint/no-unused-vars */
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { InputHTMLAttributes } from 'react';
import { colors, spacing } from '../../../../utils/styleguide';
import Icon from '../../Icon';
import Typography, { styles } from '../../text/Typography';

const iconProps = {
  disabled: {
    name: 'lock',
  },
  error: {
    name: 'x-filled',
    fill: colors.red,
  },
  success: {
    name: 'check-filled',
    fill: colors.green,
  },
};

const getIcon = (hasError?: boolean, hasSuccess?: boolean, disabled?: boolean) => {
  let variantToRender;
  if (disabled) {
    variantToRender = 'disabled';
  } else if (hasError) {
    variantToRender = 'error';
  } else if (hasSuccess) {
    variantToRender = 'success';
  }
  return (
    variantToRender && (
      <Icon
        css={css`
          position: absolute;
          right: 16px;
          top: 20px;
          pointer-events: none;
        `}
        // @ts-expect-error ts-migrate(7053) FIXME: No index signature with a parameter of type 'strin... Remove this comment to see the full error message
        {...iconProps[variantToRender]}
        size="16"
      />
    )
  );
};

type BaseTextProps = {
  value?: string;
  multiline?: boolean;
  hasFocus?: boolean;
  hasError?: boolean;
  hasSuccess?: boolean;
  straightRightBorder?: boolean;
  skipLeftBorder?: boolean;
  maxLength?: number;
} & InputHTMLAttributes<HTMLElement>;

/* eslint-disable @typescript-eslint/no-unused-vars */
const BaseText = ({
  value,
  multiline,
  hasFocus,
  hasError,
  hasSuccess,
  straightRightBorder,
  skipLeftBorder,
  ...rest
}: /* eslint-enable @typescript-eslint/no-unused-vars */
BaseTextProps) => {
  if (multiline) {
    // https://bugs.chromium.org/p/chromium/issues/detail?id=81560
    return (
      <div
        className={rest.className}
        css={css`
          padding-right: 0 !important;
          padding-left: 0 !important;
          padding-bottom: 0 !important;
        `}
      >
        <textarea
          css={css`
            padding-top: 0 !important;
            border: none !important;
            resize: vertical;
          `}
          rows={value ? value.split('\n').length : 1}
          {...rest}
          value={value}
        />
      </div>
    );
  }
  return <input {...rest} value={value} />;
};

const Input = styled(BaseText)`
  ${styles.inputField}
  color: ${({ disabled }) => (disabled ? colors.grey : colors.black)};
  outline: none;
  margin: 0;
  width: 100%;
  border: 1px solid ${colors.grey};
  border-left-width: ${({ skipLeftBorder }) => (skipLeftBorder ? 0 : 1)};
  transition: border-color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
  background-color: ${colors.white};
  padding: ${spacing[4]}px ${spacing[3]}px ${spacing[1]}px;
  height: ${({ multiline }) => (multiline ? 'auto' : '24px')};
  min-height: ${({ multiline }) => (multiline ? '96px' : '24px')};
  position: relative;
  display: flex;
  align-items: flex-end;
  min-width: 0;
  ${(props) =>
    props.type === 'password' &&
    css`
      font-family: caption;
      letter-spacing: 1px;
    `}

  ${({ straightRightBorder, skipLeftBorder }) => {
    if (straightRightBorder) {
      return css`
        border-radius: 2px 0 0 2px;
      `;
    } else if (skipLeftBorder) {
      return css`
        border-radius: 0 2px 2px 0;
      `;
    }
    return css`
      border-radius: 4px;
    `;
  }}

  ${({ hasFocus, hasError, hasSuccess, disabled, skipLeftBorder }) => {
    let borderColor = colors.black;
    const borderWidth = getBorderWidth({ hasFocus, hasError });
    let paddingOffset = 1;

    if (hasError) borderColor = colors.red;
    else if (hasSuccess) {
      borderColor = colors.green;
      paddingOffset = 0;
    } else if (disabled) {
      borderColor = colors.lightGrey;
      paddingOffset = 0;
    }

    return (
      (hasFocus || hasError || hasSuccess || disabled) &&
      css`
        border-color: ${borderColor};
        border-width: ${borderWidth};
        padding: ${spacing[4] - paddingOffset}px ${spacing[3] - paddingOffset}px
          ${spacing[1] - paddingOffset}px;
        border-left-width: ${skipLeftBorder ? 0 : borderWidth};
      `
    );
  }}
`;

const getBorderWidth = ({ hasFocus, hasError }: { hasFocus?: boolean; hasError?: boolean }) =>
  hasFocus || hasError ? '2px' : '1px';

type InputBaseProps = {
  className?: string;
  disabled?: boolean;
  error?: string;
  successMessage?: string;
  showSuccess?: boolean;
  instructions?: string;
  labelLifted?: boolean;
  label?: string;
  type?: string;
  hasFocus?: boolean;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onInvalid?: (event: React.InvalidEvent<HTMLInputElement>) => void;
  multiline?: boolean;
  name?: string;
  required?: boolean;
  straightRightBorder?: boolean;
  skipLeftBorder?: boolean;
  maxLength?: number;
  autoComplete?: string;
  autoFocus?: boolean;
  placeholder?: string;
};

const InputBase = ({
  className,
  disabled,
  error,
  successMessage,
  showSuccess,
  instructions,
  hasFocus,
  labelLifted,
  label,
  type,
  value,
  onChange,
  onFocus,
  onBlur,
  onInvalid,
  multiline,
  name,
  required,
  straightRightBorder,
  skipLeftBorder,
  maxLength,
  autoComplete,
  autoFocus,
  placeholder,
}: InputBaseProps) => {
  let labelColor;
  let infoTextColor;

  if (disabled) labelColor = colors.grey;
  else if (error) labelColor = colors.red;
  else if (successMessage || showSuccess) labelColor = colors.green;
  else if (labelLifted) labelColor = colors.black;
  else labelColor = colors.darkGrey;

  if (error) infoTextColor = colors.red;
  else if (successMessage || showSuccess) infoTextColor = colors.green;
  else infoTextColor = colors.darkGrey;

  return (
    <div
      className={className}
      css={css`
        margin-bottom: ${error ? '13' : spacing[5]}px;
      `}
    >
      <label
        css={css`
          position: relative;
          display: flex;
        `}
      >
        <div
          css={css`
            pointer-events: none;
            ${styles.label}
            color: ${labelColor};
            z-index: 1;
            position: absolute;
            top: 0;
            left: 0;
            transform-origin: top left;
            transform: ${labelLifted
              ? 'translate(16px, 8px) scale(0.71)'
              : 'translate(16px, 16px) scale(1)'};
            font-weight: ${labelLifted ? '500' : '400'};
            transition: color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms,
              transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
          `}
        >
          {label}
        </div>
        <Input
          type={type}
          hasFocus={hasFocus}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          hasError={!!error}
          hasSuccess={!!successMessage || showSuccess}
          disabled={disabled}
          multiline={multiline}
          name={name}
          required={required}
          straightRightBorder={straightRightBorder}
          skipLeftBorder={skipLeftBorder}
          maxLength={maxLength}
          onInvalid={onInvalid}
          {...(autoComplete && { autoComplete: autoComplete })}
          autoFocus={autoFocus}
          placeholder={placeholder}
        />
        {getIcon(!!error, !!successMessage || showSuccess, disabled)}
      </label>
      {(error || successMessage || instructions) && (
        <Typography useBuiltInMargins variant="smallText" tag="span" color={infoTextColor}>
          {`${error ? error : successMessage || instructions}`}
        </Typography>
      )}
    </div>
  );
};

export default InputBase;
